import {
    complianceHealthPresentation,
    normalizeComplianceIntegration,
} from "../../lib/complianceIntegrationHealth.js";
import { LOGO_DEFS } from "../../lib/dimensionRenderers.js";
import { TONE } from "../../components/statusCard.jsx";

// LangSmith is LangChain's product and carries its mark. From Simple Icons
// (CC0-1.0):
// https://github.com/simple-icons/simple-icons/blob/8f56a0b75ca568d873cde654c11728507621c689/icons/langchain.svg
const LANGCHAIN_LOGO = {
    viewBox: "0 0 24 24",
    paths: [
        "M7.53 15.975a7.53 7.53 0 0 0 2.206-5.325A7.54 7.54 0 0 0 7.53 5.325L2.205 0A7.54 7.54 0 0 0 0 5.325a7.54 7.54 0 0 0 2.205 5.325zm11.144.493a7.54 7.54 0 0 0-5.325-2.206 7.54 7.54 0 0 0-5.325 2.206l5.325 5.325a7.54 7.54 0 0 0 5.325 2.205A7.54 7.54 0 0 0 24 21.793zM2.219 21.78a7.54 7.54 0 0 0 5.325 2.205v-7.53H.014a7.54 7.54 0 0 0 2.205 5.325M20.73 8.595a7.53 7.53 0 0 0-5.327-2.206 7.53 7.53 0 0 0-5.325 2.207l5.325 5.325z",
    ],
};

export const SOURCES = [
    {
        key: "anthropic",
        name: "Anthropic",
        blurb: "claude.ai and Claude Desktop chats",
        logo: LOGO_DEFS.claude,
    },
    {
        key: "openai",
        name: "OpenAI",
        blurb: "ChatGPT conversations, app and MCP calls, and Codex sessions",
        logo: LOGO_DEFS.chatgpt,
    },
    {
        key: "perplexity",
        name: "Perplexity Enterprise",
        blurb: "Prompts, responses, and automated agent actions",
        logo: LOGO_DEFS.perplexity,
    },
    {
        key: "langsmith",
        name: "LangSmith",
        blurb: "LangChain and LangGraph agents your teams build",
        logo: LANGCHAIN_LOGO,
    },
    {
        key: "copilot",
        name: "Microsoft 365 Copilot",
        blurb: "Word, Excel, Outlook, Teams, and Copilot Chat",
        logo: LOGO_DEFS.m365copilot,
    },
    {
        key: "vertex",
        name: "Google Vertex AI",
        blurb: "Gemini, Claude, Llama and other model calls your Google Cloud projects make",
        logo: LOGO_DEFS.vertex,
    },
    {
        key: "litellm",
        name: "LiteLLM",
        blurb: "Every model call your teams route through LiteLLM",
        logo: LOGO_DEFS.litellm,
    },
    {
        key: "foundry",
        name: "Microsoft Foundry",
        blurb: "Model calls through your API Management AI gateway, and traced Foundry agents",
        logo: LOGO_DEFS["microsoft-foundry"],
    },
];

export function sourceByKey(key) {
    return SOURCES.find((source) => source.key === key) ?? null;
}

// Wire normalizers (grpc-gateway emits camelCase; tolerate snake_case).

export function keyIntegrationSummary(response) {
    return {
        ...normalizeComplianceIntegration(response),
        lastSyncError: response?.lastSyncError ?? response?.last_sync_error ?? "",
    };
}

export function openaiIntegration(raw) {
    return {
        id: raw?.id ?? "",
        status: raw?.status ?? "",
        principalId: raw?.principalId ?? raw?.principal_id ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
    };
}

export function perplexitySummary(response) {
    return {
        configured: response?.configured ?? false,
        status: response?.status ?? "",
        webhookUrl: response?.webhookUrl ?? response?.webhook_url ?? "",
        webhookToken: response?.webhookToken ?? response?.webhook_token ?? "",
        previousCredentialValidUntil:
            response?.previousCredentialValidUntil ??
            response?.previous_credential_valid_until ??
            null,
        rawRetentionDays: response?.rawRetentionDays ?? response?.raw_retention_days ?? 0,
    };
}

export function langsmithIntegration(raw) {
    return {
        id: raw?.id ?? "",
        status: raw?.status ?? "",
        workspaceId: raw?.workspaceId ?? raw?.workspace_id ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
        watchedProjects: (raw?.watchedProjects ?? raw?.watched_projects ?? []).map((project) => ({
            projectId: project?.projectId ?? project?.project_id ?? "",
            ruleId: project?.ruleId ?? project?.rule_id ?? "",
            lastWindowEnd: project?.lastWindowEnd ?? project?.last_window_end ?? null,
        })),
    };
}

export function copilotIntegration(raw) {
    return {
        id: raw?.id ?? "",
        status: raw?.status ?? "",
        managed: !!raw?.managed,
        displayName: raw?.displayName ?? raw?.display_name ?? "",
        entraTenantId: raw?.entraTenantId ?? raw?.entra_tenant_id ?? "",
        deliveryMode: raw?.deliveryMode ?? raw?.delivery_mode ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
        lastSuccessAt: raw?.lastSuccessAt ?? raw?.last_success_at ?? null,
        createdAt: raw?.createdAt ?? raw?.created_at ?? null,
    };
}

export function vertexIntegration(raw) {
    return {
        id: raw?.id ?? "",
        status: raw?.status ?? "",
        organizationId: raw?.organizationId ?? raw?.organization_id ?? "",
        datasetProjectId: raw?.datasetProjectId ?? raw?.dataset_project_id ?? "",
        datasetId: raw?.datasetId ?? raw?.dataset_id ?? "",
        serviceAccountEmail: raw?.serviceAccountEmail ?? raw?.service_account_email ?? "",
        projectIds: raw?.projectIds ?? raw?.project_ids ?? [],
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
        lastSuccessAt: raw?.lastSuccessAt ?? raw?.last_success_at ?? null,
        uncapturedModels: (raw?.uncapturedModels ?? raw?.uncaptured_models ?? []).map((model) => ({
            projectId: model?.projectId ?? model?.project_id ?? "",
            location: model?.location ?? "",
            model: model?.model ?? "",
            reason: model?.reason ?? "",
            state: model?.state ?? "",
        })),
    };
}

export function litellmIntegration(raw) {
    return {
        id: raw?.id ?? "",
        displayName: raw?.displayName ?? raw?.display_name ?? "",
        status: raw?.status ?? "",
        webhookUrl: raw?.webhookUrl ?? raw?.webhook_url ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastDeliveryAt: raw?.lastDeliveryAt ?? raw?.last_delivery_at ?? null,
        lastSuccessAt: raw?.lastSuccessAt ?? raw?.last_success_at ?? null,
        previousCredentialValidUntil:
            raw?.previousCredentialValidUntil ?? raw?.previous_credential_valid_until ?? null,
    };
}

// grpc-gateway writes int64 counts as JSON strings.
function count(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

export function foundryWorkspace(raw) {
    return {
        workspaceResourceId: raw?.workspaceResourceId ?? raw?.workspace_resource_id ?? "",
        customerId: raw?.customerId ?? raw?.customer_id ?? "",
        status: raw?.status ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
        lastSuccessAt: raw?.lastSuccessAt ?? raw?.last_success_at ?? null,
        addedAt: raw?.addedAt ?? raw?.added_at ?? null,
        missingRole: Boolean(raw?.missingRole ?? raw?.missing_role),
        gatewayLogsMissing: Boolean(raw?.gatewayLogsMissing ?? raw?.gateway_logs_missing),
        tables: (raw?.tables ?? []).map((table) => ({
            name: table?.name ?? "",
            state: table?.state ?? "",
            readThrough: table?.readThrough ?? table?.read_through ?? null,
        })),
        uncaptured: (raw?.uncaptured ?? []).map((entry) => ({
            resourceId: entry?.resourceId ?? entry?.resource_id ?? "",
            calls: count(entry?.calls),
        })),
        unsupported: (raw?.unsupported ?? []).map((entry) => ({
            route: entry?.route ?? "",
            calls: count(entry?.calls),
        })),
        callers: (raw?.callers ?? []).map((entry) => ({
            resourceId: entry?.resourceId ?? entry?.resource_id ?? "",
            deployment: entry?.deployment ?? "",
            callerObjectId: entry?.callerObjectId ?? entry?.caller_object_id ?? "",
            calls: count(entry?.calls),
        })),
    };
}

// The List response: each connection, and beside them the client ID of Origin's
// managed app, which a managed connection signs in as and so is granted the
// workspace role. A bring-your-own client ID is write-only on the server, so
// that connection's `clientId` stays empty. An empty managed client ID means this
// environment has no managed app to sign in with; an older backend sends none at
// all, and its environment keeps offering managed sign-in.
export function foundryListing(listed) {
    const managedClientId = listed?.managedClientId ?? listed?.managed_client_id;
    return {
        integrations: (listed?.integrations ?? []).map((raw) => foundryIntegration(raw, managedClientId ?? "")),
        managedSignIn: managedClientId !== "",
    };
}

export function foundryIntegration(raw, managedClientId = "") {
    const managed = Boolean(raw?.managed);
    return {
        id: raw?.id ?? "",
        status: raw?.status ?? "",
        managed,
        clientId: managed ? managedClientId : "",
        entraTenantId: raw?.entraTenantId ?? raw?.entra_tenant_id ?? "",
        organizationName: raw?.organizationName ?? raw?.organization_name ?? "",
        lastSyncError: raw?.lastSyncError ?? raw?.last_sync_error ?? "",
        lastPollAt: raw?.lastPollAt ?? raw?.last_poll_at ?? null,
        lastSuccessAt: raw?.lastSuccessAt ?? raw?.last_success_at ?? null,
        createdAt: raw?.createdAt ?? raw?.created_at ?? null,
        workspaces: (raw?.workspaces ?? []).map(foundryWorkspace),
    };
}

// The List RPC is answered only by a backend that has the connector; an older one
// answers unimplemented, and the card says the source is not offered yet.
export const FOUNDRY_NOT_OFFERED = Object.freeze({ notOffered: true });

export function foundryNotOffered(err) {
    return err?.grpcCode === "UNIMPLEMENTED" || err?.status === 404 || err?.status === 501;
}

// The app a connection signs in as: the one the admin assigns the workspace role to.
export function foundryAppName(managed, { sentenceStart = false } = {}) {
    if (managed) return "Origin's app";
    return sentenceStart ? "Your app registration" : "your app registration";
}

export function foundryMissingRole(managed) {
    return `The workspace receives data Origin cannot read. Grant ${foundryAppName(managed)} Privileged Monitoring Data Reader on the workspace itself, not on the Application Insights resource. A new assignment takes a few minutes to apply.`;
}

export const FOUNDRY_GATEWAY_LOGS_MISSING =
    "API Management sends its LLM logs here but not its gateway logs, so no gateway call can be captured. Tick Logs related to ApiManagement Gateway in the instance's diagnostic setting.";

export function foundryConnectionName(integration) {
    return integration.organizationName || integration.entraTenantId || "";
}

export function foundryWorkspaceProblem(workspace, managed) {
    if (workspace.status === "error") return workspace.lastSyncError || "The last read of this workspace failed.";
    if (workspace.missingRole) return foundryMissingRole(managed);
    if (workspace.gatewayLogsMissing) return FOUNDRY_GATEWAY_LOGS_MISSING;
    return "";
}

function workspaceProblems(integration) {
    return integration.workspaces
        .map((workspace) => foundryWorkspaceProblem(workspace, integration.managed))
        .filter(Boolean);
}

function foundryConnectionFailing(integration) {
    return (
        integration.status === "error" ||
        integration.status === "disabled" ||
        (Boolean(integration.lastSyncError) && workspaceProblems(integration).length === 0)
    );
}

export function foundryProblem(integration) {
    const [workspaceProblem] = workspaceProblems(integration);
    if (workspaceProblem) return workspaceProblem;
    if (integration.lastSyncError) return integration.lastSyncError;
    if (integration.status === "disabled") return "Capture is disabled for this connection.";
    if (integration.status === "error") return "The last sync failed.";
    return "";
}

export function foundryNeedsAttention(integration) {
    return foundryConnectionFailing(integration) || workspaceProblems(integration).length > 0;
}

// An Entra directory connected through Origin's app means this tenant has already consented to the
// app, so signing in with Microsoft adds nothing for it to approve.
export function consentedThroughOriginsApp(directory) {
    if (!directory?.loaded || directory.data == null) return false;
    return (directory.data.integrations ?? []).some(
        (integration) =>
            integration.provider === "ENTRA_ID" &&
            integration.managed === true &&
            integration.status !== "error" &&
            !INACTIVE_DIRECTORY_STATUSES.has(integration.status),
    );
}

function foundryReadSomething(integration) {
    return integration.workspaces.some((workspace) => workspace.tables.some((table) => table.readThrough));
}

// Connect, consent, add a workspace, send data to it, capture.
export function foundrySteps(integration) {
    if (!integration) return ["current", "todo", "todo", "todo", "todo"];
    const attention = foundryNeedsAttention(integration);
    if (integration.status === "pending") return ["done", attention ? "failed" : "current", "todo", "todo", "todo"];
    const consent = foundryConnectionFailing(integration) ? "failed" : "done";
    if (integration.workspaces.length === 0) return ["done", consent, "current", "todo", "todo"];
    const read = foundryReadSomething(integration);
    return ["done", consent, "done", read ? "done" : "current", attention ? "failed" : read ? "done" : "current"];
}

export function vertexBlocked(integration) {
    return integration.uncapturedModels.filter((model) => model.state !== "pending");
}

export function vertexTurningOn(integration) {
    return integration.uncapturedModels.filter((model) => model.state === "pending");
}

export function directoryScoped(directory) {
    if (!directory?.loaded || directory.data == null) return false;
    const active = (directory.data.integrations ?? []).filter(
        (integration) => !INACTIVE_DIRECTORY_STATUSES.has(integration.status),
    );
    return (
        active.some((integration) => integration.syncScope === "scoped") &&
        !active.some((integration) => integration.syncScope === "whole_tenant")
    );
}

export function vertexSteps(integration) {
    if (!integration) return ["current", "todo", "todo"];
    const logging =
        vertexBlocked(integration).length > 0
            ? "failed"
            : vertexTurningOn(integration).length > 0
              ? "current"
              : "done";
    const capture = integration.lastSyncError ? "failed" : integration.lastSuccessAt ? "done" : "current";
    return ["done", logging, capture];
}

export function copilotConnectionName(integration) {
    return integration.displayName || integration.entraTenantId || "";
}

const INACTIVE_DIRECTORY_STATUSES = new Set(["pending", "disabled", "deleting"]);

export function hasActiveDirectory(response) {
    return (response?.integrations ?? []).some(
        (integration) => !INACTIVE_DIRECTORY_STATUSES.has(integration.status),
    );
}

// What a non-real-time delivery mode means for the admin, without the
// mechanism. Null when delivery is real time or not yet known.
export function deliveryDelayNote(mode) {
    return mode === "polling" || mode === "polling_contended"
        ? "Activity arrives about every five minutes instead of in real time."
        : null;
}

export function whenLabel(value) {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
}

// Setup steps and health.
//
// Step states: "done", "current" (the next thing to do), "failed" (done once
// but now broken, or a check that fails), "todo" (blocked behind an earlier
// step). The first step that is not done is where capture stops.

export function keyIntegrationSteps(summary, sourceKey = null) {
    const configured = summary.configured;
    let sync = "todo";
    if (configured) {
        if (sourceKey === "anthropic" && summary.healthStatus) {
            if (summary.healthStatus === "healthy") sync = "done";
            else if (summary.healthStatus === "action_required") sync = "failed";
            else sync = "current";
        } else {
            if (summary.status === "connected") sync = "done";
            else if (summary.status === "error" || summary.status === "disabled") sync = "failed";
            else sync = "current";
        }
    }
    return [configured ? "done" : "current", configured ? "done" : "todo", sync];
}

export function perplexitySteps(summary) {
    if (!summary.configured) return ["current", "todo"];
    return ["done", summary.status === "error" || summary.status === "disabled" ? "failed" : "done"];
}

export function openaiNeedsAttention(integration) {
    return (
        integration.status === "error" ||
        integration.status === "disabled" ||
        Boolean(integration.lastSyncError)
    );
}

export function openaiSteps(integration) {
    if (!integration) return ["current", "todo", "todo"];
    if (openaiNeedsAttention(integration)) return ["done", "done", "failed"];
    return ["done", "done", integration.lastPollAt ? "done" : "current"];
}

export function langsmithNeedsAttention(integration) {
    return (
        integration.status === "error" ||
        integration.status === "disabled" ||
        Boolean(integration.lastSyncError)
    );
}

export function langsmithSteps(integration) {
    if (!integration) return ["current", "todo", "todo", "todo"];
    const watched = integration.watchedProjects.length > 0;
    let delivery = "todo";
    if (watched) {
        if (langsmithNeedsAttention(integration)) delivery = "failed";
        else if (integration.status === "connected") delivery = "done";
        else delivery = "current";
    }
    return ["done", "done", watched ? "done" : "current", delivery];
}

export function copilotNeedsAttention(integration) {
    return (
        integration.status === "error" ||
        integration.status === "disabled" ||
        Boolean(integration.lastSyncError)
    );
}

export function copilotSteps(integration) {
    if (!integration) return ["current", "todo", "todo"];
    const attention = copilotNeedsAttention(integration);
    if (integration.status === "pending") return ["done", attention ? "failed" : "current", "todo"];
    return ["done", "done", attention ? "failed" : "done"];
}

export function litellmNeedsAttention(integration) {
    return (
        integration.status === "error" ||
        integration.status === "disabled" ||
        Boolean(integration.lastSyncError)
    );
}

export function litellmSteps(integration) {
    if (!integration) return ["current", "todo", "todo"];
    if (litellmNeedsAttention(integration)) return ["done", "done", "failed"];
    return ["done", "done", integration.lastDeliveryAt ? "done" : "current"];
}

export function stepProgress(states) {
    const index = states.findIndex((state) => state !== "done");
    return {
        total: states.length,
        done: states.filter((state) => state === "done").length,
        stuckAt: index === -1 ? null : index + 1,
        stuckState: index === -1 ? null : states[index],
    };
}

export function openaiProblem(integration) {
    if (integration.lastSyncError) return integration.lastSyncError;
    if (integration.status === "disabled") return "Capture is disabled for this workspace.";
    if (integration.status === "error") return "The last sync failed.";
    return "";
}

function openaiHealth(integrations) {
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const failing = integrations.filter(openaiNeedsAttention);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 workspace failing" : `${failing.length} workspaces failing`,
            detail: openaiProblem(failing[0]),
        };
    }
    if (integrations.some((integration) => !integration.lastPollAt)) {
        return { tone: TONE.WAITING, label: "Waiting for first sync", detail: "" };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

function anthropicIntegrationHealth(summary) {
    const presentation = complianceHealthPresentation(summary);
    if (!summary.configured) {
        return { tone: TONE.SETUP, label: presentation.label, detail: presentation.impact };
    }
    const tone = {
        healthy: TONE.OK,
        warning: TONE.WAITING,
        critical: TONE.ATTENTION,
        neutral: TONE.WAITING,
    }[presentation.tone];
    return { tone, label: presentation.label, detail: presentation.impact };
}

function perplexityHealth(summary) {
    if (!summary.configured) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    if (summary.status === "error") {
        return {
            tone: TONE.ATTENTION,
            label: "Deliveries failing",
            detail: "Check the webhook URL and bearer token in the Perplexity admin console.",
        };
    }
    if (summary.status === "disabled") {
        return { tone: TONE.ATTENTION, label: "Disabled", detail: "The webhook is not accepting events." };
    }
    return { tone: TONE.OK, label: "Webhook ready", detail: "" };
}

function langsmithHealth(integrations) {
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const failing = integrations.filter(langsmithNeedsAttention);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 workspace failing" : `${failing.length} workspaces failing`,
            detail: failing[0].lastSyncError || "LangSmith deliveries are failing.",
        };
    }
    const unwatched = integrations.filter((integration) => integration.watchedProjects.length === 0);
    if (unwatched.length > 0) {
        return {
            tone: TONE.WAITING,
            label: "No projects watched",
            detail:
                unwatched.length === 1
                    ? "Choose which tracing projects to capture."
                    : `${unwatched.length} workspaces have no projects watched.`,
        };
    }
    if (integrations.some((integration) => integration.status !== "connected")) {
        return { tone: TONE.WAITING, label: "Waiting for first run", detail: "" };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

export function copilotProblem(integration) {
    if (integration.lastSyncError) return integration.lastSyncError;
    if (integration.status === "disabled") return "Capture is disabled for this connection.";
    if (integration.status === "error") return "The last sync failed.";
    return "";
}

function copilotHealth(integrations) {
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const failing = integrations.filter(copilotNeedsAttention);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 connection failing" : `${failing.length} connections failing`,
            detail: copilotProblem(failing[0]),
        };
    }
    if (integrations.some((integration) => integration.status !== "connected")) {
        return { tone: TONE.WAITING, label: "Waiting for admin consent", detail: "" };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

function vertexHealth(integrations) {
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const live = integrations.filter((integration) => integration.status !== "deleting");
    if (live.length === 0) return { tone: TONE.WAITING, label: "Being removed", detail: "" };
    const failing = live.filter((integration) => integration.lastSyncError);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 org failing" : `${failing.length} orgs failing`,
            detail: failing[0].lastSyncError,
        };
    }
    const blocked = live.flatMap(vertexBlocked);
    if (blocked.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: blocked.length === 1 ? "1 model not captured" : `${blocked.length} models not captured`,
            detail: blocked[0].reason,
        };
    }
    if (live.some((integration) => !integration.lastSuccessAt)) {
        return { tone: TONE.WAITING, label: "Waiting for first read", detail: "" };
    }
    if (live.some((integration) => vertexTurningOn(integration).length > 0)) {
        return { tone: TONE.WAITING, label: "Turning on prompt logging", detail: "" };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

function foundryHealth(listing) {
    if (listing === FOUNDRY_NOT_OFFERED) {
        return {
            tone: TONE.UNAVAILABLE,
            label: "Not available yet",
            detail: "This Origin environment does not offer Microsoft Foundry yet.",
        };
    }
    const { integrations } = listing;
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const failing = integrations.filter(foundryConnectionFailing);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 connection failing" : `${failing.length} connections failing`,
            detail: foundryProblem(failing[0]),
        };
    }
    const troubled = integrations.flatMap(workspaceProblems);
    if (troubled.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: troubled.length === 1 ? "1 workspace needs attention" : `${troubled.length} workspaces need attention`,
            detail: troubled[0],
        };
    }
    if (integrations.some((integration) => integration.status === "pending")) {
        return { tone: TONE.WAITING, label: "Waiting for admin consent", detail: "" };
    }
    if (integrations.some((integration) => integration.workspaces.length === 0)) {
        return {
            tone: TONE.WAITING,
            label: "No workspace added",
            detail: "Add the Log Analytics workspace that receives your gateway logs or agent traces.",
        };
    }
    if (!integrations.some(foundryReadSomething)) {
        return {
            tone: TONE.WAITING,
            label: "No data yet",
            detail: "Nothing has reached the workspace yet. A new diagnostic setting can take up to 90 minutes to write its first row.",
        };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

function litellmHealth(integrations) {
    if (integrations.length === 0) return { tone: TONE.SETUP, label: "Not set up", detail: "" };
    const failing = integrations.filter(litellmNeedsAttention);
    if (failing.length > 0) {
        return {
            tone: TONE.ATTENTION,
            label: failing.length === 1 ? "1 connection failing" : `${failing.length} connections failing`,
            detail: failing[0].lastSyncError || "LiteLLM deliveries are failing.",
        };
    }
    if (integrations.some((integration) => !integration.lastDeliveryAt)) {
        return { tone: TONE.WAITING, label: "Waiting for first call", detail: "" };
    }
    return { tone: TONE.OK, label: "Capturing", detail: "" };
}

export const NO_DIRECTORY = {
    tone: TONE.ATTENTION,
    label: "No directory connected",
    detail: "Nothing is captured until a directory from Entra, Okta or Google is connected to decide who is in scope.",
};

export function directoryMissing(directory) {
    return Boolean(directory?.loaded) && directory.data != null && !hasActiveDirectory(directory.data);
}

export function scopeCaptureStep(states, directory) {
    const noDirectory = states.at(-1) === "done" && directoryMissing(directory);
    return { states: noDirectory ? [...states.slice(0, -1), "failed"] : states, noDirectory };
}

const UNSCOPED_TONES = new Set([TONE.SETUP, TONE.LOADING, TONE.UNAVAILABLE]);

export function sourceHealth(key, entry, directory = null) {
    const health = connectorHealth(key, entry);
    if (UNSCOPED_TONES.has(health.tone)) return health;
    if (directoryMissing(directory)) return NO_DIRECTORY;
    return health;
}

function connectorHealth(key, entry) {
    if (!entry || (!entry.loaded && entry.data == null)) {
        return { tone: TONE.LOADING, label: "Loading", detail: "" };
    }
    if (entry.data == null) {
        return {
            tone: TONE.UNAVAILABLE,
            label: "Status unavailable",
            detail: entry.error || "Could not load this source.",
        };
    }
    switch (key) {
        case "anthropic":
            return anthropicIntegrationHealth(entry.data);
        case "openai":
            return openaiHealth(entry.data);
        case "perplexity":
            return perplexityHealth(entry.data);
        case "langsmith":
            return langsmithHealth(entry.data);
        case "copilot":
            return copilotHealth(entry.data);
        case "vertex":
            return vertexHealth(entry.data);
        case "litellm":
            return litellmHealth(entry.data);
        case "foundry":
            return foundryHealth(entry.data);
        default:
            return { tone: TONE.UNAVAILABLE, label: "Unknown source", detail: "" };
    }
}
